/**
 * Link prefetching composable - McMaster-Carr's signature technique
 *
 * When a user hovers over a link, this prefetches the destination page's
 * HTML/resources in the background. If they click, the page loads instantly.
 *
 * Usage in components:
 *   import { usePrefetch } from '@/composables/usePrefetch'
 *   const { prefetchOnHover, prefetchRoute } = usePrefetch()
 *
 *   <router-link
 *     to="/dashboard"
 *     @mouseenter="prefetchRoute('/dashboard')"
 *   >Dashboard</router-link>
 */

import { ref } from 'vue'
import { useRouter } from 'vue-router'

// Track which routes have been prefetched to avoid duplicates
const prefetchedRoutes = new Set()

// Track in-flight prefetch requests
const pendingPrefetches = new Map()

export function usePrefetch() {
  const router = useRouter()

  /**
   * Prefetch a route's component when user hovers over a link
   * @param {string} path - The route path to prefetch (e.g., '/dashboard')
   */
  const prefetchRoute = async (path) => {
    // Skip if already prefetched or currently prefetching
    if (prefetchedRoutes.has(path) || pendingPrefetches.has(path)) {
      return
    }

    // Find the matching route
    const matchedRoute = router.resolve(path)
    if (!matchedRoute || !matchedRoute.matched.length) {
      return
    }

    // Mark as pending
    pendingPrefetches.set(path, true)

    try {
      // Get all matched route components and prefetch them
      const componentPromises = matchedRoute.matched
        .filter(record => record.components?.default)
        .map(record => {
          const component = record.components.default
          // If it's a lazy-loaded component (function), call it to trigger loading
          if (typeof component === 'function') {
            return component()
          }
          return Promise.resolve(component)
        })

      await Promise.all(componentPromises)

      // Mark as successfully prefetched
      prefetchedRoutes.add(path)
    } catch (error) {
      // Silently fail - prefetch is an optimization, not critical
      console.debug('[Prefetch] Failed to prefetch:', path, error)
    } finally {
      pendingPrefetches.delete(path)
    }
  }

  /**
   * Directive-style handler for mouseenter events
   * Can be used as: @mouseenter="prefetchOnHover('/path')"
   * Or bound to event: @mouseenter="() => prefetchOnHover('/path')"
   */
  const prefetchOnHover = (path) => {
    // Small delay to avoid prefetching on accidental hovers
    setTimeout(() => {
      prefetchRoute(path)
    }, 50)
  }

  /**
   * Prefetch multiple routes at once (e.g., on page load for likely destinations)
   * @param {string[]} paths - Array of route paths to prefetch
   */
  const prefetchRoutes = async (paths) => {
    // Use requestIdleCallback if available for non-blocking prefetch
    const prefetchFn = () => {
      paths.forEach(path => prefetchRoute(path))
    }

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(prefetchFn, { timeout: 2000 })
    } else {
      setTimeout(prefetchFn, 100)
    }
  }

  /**
   * Create a prefetch link element for HTML documents
   * Useful for external pages or non-SPA navigation
   * @param {string} url - Full URL to prefetch
   */
  const prefetchUrl = (url) => {
    if (document.querySelector(`link[rel="prefetch"][href="${url}"]`)) {
      return // Already exists
    }

    const link = document.createElement('link')
    link.rel = 'prefetch'
    link.href = url
    link.as = 'document'
    document.head.appendChild(link)
  }

  /**
   * DNS prefetch for external domains
   * @param {string} domain - Domain to prefetch DNS for (e.g., 'api.twitch.tv')
   */
  const dnsPrefetch = (domain) => {
    if (document.querySelector(`link[rel="dns-prefetch"][href="//${domain}"]`)) {
      return
    }

    const link = document.createElement('link')
    link.rel = 'dns-prefetch'
    link.href = `//${domain}`
    document.head.appendChild(link)
  }

  return {
    prefetchRoute,
    prefetchOnHover,
    prefetchRoutes,
    prefetchUrl,
    dnsPrefetch,
    prefetchedRoutes // Expose for debugging
  }
}

/**
 * Vue directive for automatic prefetch on hover
 *
 * Usage:
 *   <router-link v-prefetch to="/dashboard">Dashboard</router-link>
 *
 * Register globally in main.js:
 *   import { vPrefetch } from '@/composables/usePrefetch'
 *   app.directive('prefetch', vPrefetch)
 */
export const vPrefetch = {
  mounted(el, binding, vnode) {
    // Safely access router from app context - may be null during initial mount
    if (!vnode.appContext?.config?.globalProperties?.$router) return

    const router = vnode.appContext.config.globalProperties.$router
    const path = binding.value || el.getAttribute('href') || el.getAttribute('to')

    if (!path) return

    let timeoutId = null

    el.addEventListener('mouseenter', () => {
      // Small delay to avoid prefetching on accidental hovers
      timeoutId = setTimeout(async () => {
        if (prefetchedRoutes.has(path)) return

        const matchedRoute = router.resolve(path)
        if (!matchedRoute?.matched?.length) return

        try {
          const componentPromises = matchedRoute.matched
            .filter(record => record.components?.default)
            .map(record => {
              const component = record.components.default
              if (typeof component === 'function') {
                return component()
              }
              return Promise.resolve(component)
            })

          await Promise.all(componentPromises)
          prefetchedRoutes.add(path)
        } catch (e) {
          // Silently fail
        }
      }, 50)
    })

    el.addEventListener('mouseleave', () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    })
  }
}
